<template>
    <div class="flex flex-column justify-content-start gap-4 transcript-container">
        <div class="flex flex-column gap-1 border-bottom-1 pb-3 borderColor">
            <span class="transcript-title">Class Transcript</span>
            <div class="flex flex-wrap gap-3 text-sm text-500">
                <span><i class="pi pi-calendar mr-1"></i>{{ formatToDisplayDate(classInstance.date) }}</span>
                <span><i class="pi pi-clock mr-1"></i>{{ formatDuration(transcriptData.durationSeconds) }}</span>
                <span><i class="pi pi-globe mr-1"></i>{{ transcriptData.language }}</span>
            </div>
        </div>

        <div v-if="transcriptData.source?.recordingUrl" class="recording-bar p-3 border-round flex align-items-center justify-content-between">
            <div class="flex align-items-center gap-2">
                <i class="pi pi-video text-xl"></i>
                <span class="font-semibold text-sm">Class Recording ({{ transcriptData.source.provider }})</span>
            </div>
            <Button icon="pi pi-external-link" label="Open Recording" class="p-button-sm p-button-text" 
                    @click="openLink(transcriptData.source.recordingUrl)" />
        </div>

        <section class="transcript-body">
            <h3 class="section-header" style="color: #0D9488;">Conversation Details</h3>
            
            <div class="flex flex-column gap-4 mt-3">
                <div v-for="(segment, index) in transcriptData.segments" :key="index" 
                     class="flex gap-3 segment-row">
                    <div class="text-xs text-400 font-mono pt-1 min-w-max">
                        [{{ formatMs(segment.startMs) }}]
                    </div>
                    
                    <div class="flex flex-column gap-1">
                        <span class="speaker-label" :class="getSpeakerClass(segment.speaker)">
                            {{ segment.speaker }}
                        </span>
                        <p class="Paragraph-Medium m-0 text-700 line-height-3">
                            {{ segment.text }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <div class="flex justify-content-between align-items-center text-xs text-400 mt-4 pt-3 border-top-1 borderColor">
            <div class="flex gap-2">
                <span v-for="speaker in transcriptData.speakers" :key="speaker.label" class="capitalize">
                    ● {{ speaker.label }} ({{ speaker.role }})
                </span>
            </div>
            <span>ID: {{ transcriptData._id.slice(-6) }}</span>
        </div>
    </div>
</template>

<script setup>
import { defineProps, computed } from "vue";
import { formatToDisplayDate } from "@/utils/cleanups";
import Button from "primevue/button";

const props = defineProps({
    data: {
        type: Object,
        required: true
    }
});

const transcriptData = computed(() => props.data.transcript);
const classInstance = computed(() => props.data.classInstance);

const formatMs = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    return `${mins} minutes`;
};

const getSpeakerClass = (speaker) => {
    return speaker.toLowerCase() === 'tutor' ? 'text-teal-600' : 'text-orange-600';
};

const openLink = (url) => {
    window.open(url, '_blank');
};
</script>

<style scoped>
.transcript-container {
    max-width: 800px;
    margin: 0 auto;
}

.transcript-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c3e50;
}

.section-header {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    border-left: 4px solid currentColor;
    padding-left: 0.75rem;
}

.recording-bar {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #475569;
}

.speaker-label {
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.segment-row {
    padding-bottom: 0.5rem;
}

.borderColor {
    border-color: #eee;
}

/* Custom scrollbar for better UX within modals/containers */
.transcript-body {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 1rem;
}

.transcript-body::-webkit-scrollbar {
    width: 4px;
}
.transcript-body::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}
</style>